import React, { forwardRef, useImperativeHandle } from 'react';

import CardCommon, { CardCommonType } from '@op/components/CardCommon';
import { useRequestSteamingGpt } from '../hooks/useRequestGpt';
import { RequestFuncMapType } from '../hooks/useDefaultSummary';
import { copyIcon, fullIcon, SymbolType } from '@op/constants/chart';
import { GptCardView, LeftElementType } from '../components/GptCardView';
import {
  FailedTipsTextType,
  LoadingTextType,
  PageCardRefType,
  PromptConfirmItemType,
  PromptListType,
  ScenarioType,
  GptRequestType,
} from '../types';
import '../styles/index.scss';
import usePageCardData from '../hooks/usePageCardData';
import { DBO_T } from '@op/utils/format';
import { useConfirm } from '../hooks/useConfirm';
import useLanguage from '@op/hooks/useLanguage';
import { DEFAULT_MODAL } from '../constants';

type PageCardType = {
  // GPT的模式，主贴和评论
  scenario?: ScenarioType;
  // 过滤器条件，创建GPT的时候需要
  filter: any;
  // 返回GPT的内容
  backGPTText?: (value: string | GptRequestType) => void;
  // 停止的回调函数
  stopAction?: () => void;
  // 错误回调函数
  errorCallback?: () => void;
  // 成功的回调函数
  successAction?: () => void;
  // 返回state接口的数据
  backStateData?: (data: any) => void;
  // 是否拥有停止按钮
  hasStopBtn?: boolean;
  // 进行前置请求的函数接口以及处理方式
  requestFuncMap?: RequestFuncMapType;
  // 外部获取gpt的loading状态
  getGptLoading?: (value: boolean) => void;
  // 获取gpt是否处于，get gpt summary按钮和计算的状态
  getGptShowChildren?: (value: boolean) => void;
  // 外部传入的gpt内容
  // gptDefaultHtml?: string;
  children: (
    open: (value?: boolean) => void,
    options: { loading?: boolean; total: number; handleStop: () => void },
  ) => any;
  // 外部的state数据
  oriFeedsState?: any;
  // 是否需要关联filter变化，默认为true
  linkFilterChange?: boolean;
  // 默认的prompt列表
  promptList?: PromptListType | ((wholeGameInfo: any) => PromptListType);
  // 始终显示标题
  alwaysShowTitle?: boolean;
  // prompt弹窗confirm操作
  promptConfirm?: (
    data: PromptConfirmItemType,
    defaultAction: null | (() => void),
  ) => Promise<boolean> | boolean;
  // 是否有高级GPT的选项
  hasAdvancedSummary?: boolean;
  // loading状态下再stop按钮和进度条间添加一些元素
  loadingElement?: any;
  // 基础GPT报告是否先获取taskId,再进行回调
  needBasicTaskId?: boolean;
  // 保存事件，有保存事件，就会有保存的图标按钮
  saveClick?: () => void;
  // 左侧额外显示内容，那么GPT展示右侧，该元素展示在左侧
  leftElement?: LeftElementType;
  // 默认的prompt内容
  promptValue?: PromptConfirmItemType;
  // 是否展示Result Output选项
  hasResultOutput?: boolean;
  // prompt是否为预览模式
  previewPrompt?: boolean;
  // 状态组件是否为min模式
  isMini?: boolean;
  // Loading的文案配置
  loadingTipsText?: LoadingTextType;
  // 失败的文案
  failedTipsText?: FailedTipsTextType;
  // 隐藏图标列表
  hideIcons?: Array<SymbolType>;
  // 是否要请求prompt数据
  needRequestPrompt?: boolean;
  hideFullSubTitle?: boolean;
  hasAgent?: boolean;
  hasTotalText?: boolean;
  autoRequest?: boolean;
} & Omit<CardCommonType, 'children'>;

const summaryPrompt = {
  en: `You are a professional live-stream reconstructor in the game industry. Given chronologically ordered clip summaries from a long stream, you will reconstruct a coherent, long stream summary that strictly follows the original time flow, accurately reflects content sequencing, gameplay progression, and the streamer's emotional trajectory. Be comprehensive, faithful to the input, and avoid omissions.

## Input Data Information:
- You will be given an input list of stream clip summaries. 
- Each clip summary corresponds to a 5-minute stream segment and the summaries are arranged in chronological order.

## Guidelines on generating the long stream summary:
1. Use all information: Incorporate every clip's content; do not omit details present in the summaries. If a point recurs across clips, merge consistently rather than duplicating.

2. Chronological fidelity: The output must mirror the stream's timeline, preserving sequence of segments, gameplay milestones, pacing, and the streamer's emotional/psychological changes over time. Minimal connective tissue is allowed to ensure flow. Keep it plausible.

3. Faithfulness over invention: Do not fabricate facts not supported by the input summaries. Do not make any inferences nor assumptions.

4. Resolve conflicts transparently: If clips contradict, resolve the contradiction using logical and emotional analysis. Do not present the contradiction in the long stream summary.

5. Complete, informative and succinct: Although the long stream summary should contain all the useful information be informative, the output summary length should be below 200 words. Make the summary fluent, logical and succinct. 

## Important Notes:
- All the clips originate from the same stream session, so the streamer in the clip summries refer to the same single person. As a result, do not use "streamers" or "They" in the output summary.
- Input clips may cover only a subset of the session. Do not assume the first clip is the beginning or the last clip is the end of the stream. Do not use this expression "Throughout the stream".

## Output Instructions
- The output part should begin with the mark "### Final Result:", then arrange the long stream summary in the json format.
- You need to generate BOTH English and Chinese version of the long stream summary.
- Only the json content is needed in the returned output. Do not include any intermediate steps, analysis or additional information.
- Return empty string "" for "english_summary" and "chinese_summary" fields if no input clip summary is provided.


Output Format Example:
### Final Result:
{
    "english_summary": "Example long stream summary",
    "chinese_summary": "示例长直播总结",
}

The actual input clip summaries are:
Example Input summaries
`,

  zh: `你是一名游戏行业的专业直播重构师。给定按时间顺序排列的多个直播切片摘要，你需要重构一个连贯的长篇直播总结。这个总结必须严格遵循原始的时间线，准确反映内容顺序、游戏进程以及主播的情感变化轨迹。内容需全面、忠实于输入，并避免遗漏。

## 输入数据信息:
- 你将收到一个包含多个直播切片摘要的输入列表。
- 每个切片摘要对应一段5分钟的直播片段，并且这些摘要是按时间顺序排列的。

## 生成长篇直播总结的指南:
1. 利用所有信息：整合每个切片的内容，不要省略摘要中出现的任何细节。如果某个要点在多个切片中重复出现，应进行一致性地合并，而不是简单地重复。

2. 忠于时间顺序：输出必须反映直播的时间线，保留片段的顺序、游戏里程碑、节奏以及主播随时间的情绪/心理变化。允许使用最少的连接性词句以确保行文流畅，并保持内容合理可信。

3. 忠实优于创造：不要捏造输入摘要中未提及的事实。不要进行任何推断或假设。

4. 清晰地解决冲突：如果切片内容存在矛盾，请运用逻辑和情感分析来解决这些矛盾。不要在最终的长篇总结中呈现这些矛盾。

5. 完整、信息丰富且简洁：长篇直播总结应包含所有有用信息且内容丰富，但输出的总结长度应低于200字。总结应流畅、有逻辑且简洁。

## 重要注意事项:
- 所有的切片都源自同一场直播，因此切片摘要中的主播指的是同一个人，在输出的总结中不要使用“主播们”或“他们”这样的复数形式。
- 输入的切片可能只覆盖了整场直播的一部分。不要假设第一个切片是直播的开头，也不要假设最后一个切片是直播的结束。不要使用“在整场直播中”这样的表述。

## 输出指令
- 输出部分应以“### Final Result:”标记开头，然后以 JSON 格式组织长篇直播总结。
- 你需要同时生成英文和中文两个版本的长篇直播总结。
- 返回的输出中只需要包含 JSON 内容。不要包含任何中间步骤、分析或其他额外信息。
- 如果没有提供任何输入的切片摘要，则在 "english_summary" 和 "chinese_summary" 字段中返回空字符串 ""。


## 输出格式示例:
### Final Result:
{
"english_summary": "Example long stream summary",
"chinese_summary": "示例长直播总结",
}

实际输入的切片摘要如下：
示例输入总结 
`,
};

/**
 * 当前页面展示GPT文案的卡片
 * @param param0
 * @returns
 */
function SteamingPageCard(props: PageCardType, ref: PageCardRefType) {
  const {
    filter,
    backGPTText,
    stopAction,
    errorCallback,
    successAction,
    backStateData,
    hasStopBtn = false,
    requestFuncMap,
    children,
    title,
    gptSumaryEndSlot,
    getGptSummaryData,
    getGptLoading,
    getGptShowChildren,
    lessThanIsEmpty = 20,
    gameInfo: propsGameInfo,
    wholeGameInfo: propsWholeGameInfo,
    oriFeedsState,
    promptList,
    linkFilterChange,
    alwaysShowTitle = false,
    promptConfirm,
    hasAdvancedSummary,
    loadingElement,
    needBasicTaskId,
    saveClick,
    scenario,
    leftElement,
    promptValue,
    hasResultOutput,
    previewPrompt,
    isMini,
    loadingTipsText,
    failedTipsText,
    hideIcons = ['save'],
    needRequestPrompt,
    hideFullSubTitle,
    hasAgent,
    autoRequest,
    hasTotalText = true,
    showModelType,
    ...cardProps
  } = props;

  const { formatMessage, isEn } = useLanguage();

  const {
    setPromptInfo,
    promptInfo,
    FullDialog,
    openFullDialog,
    showChildren,
    showDefaultGpt,
    defaultGptText,
    defaultGPT,
    total,
    changeShowChildren,
    titleInfo,
  } = usePageCardData({
    filter,
    requestFuncMap,
    backStateData,
    oriFeedsState,
    getGptLoading,
    linkFilterChange,
    alwaysShowTitle,
    title,
    formatMessage,
    showModelType,
  });

  const {
    status,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    requestGptData,
    summary,
    errorType,
    loading,
  } = useRequestSteamingGpt({
    filter,
    stopAction,
    successAction,
    backGPTText,
    errorCallback,
    defaultGptText,
    showDefaultGpt,
    getGptSummaryData,
    lessThanIsEmpty,
    defaultGPT,
    autoRequest,
    changeShowChildren,
  });

  function getPageSummary(_data: PromptConfirmItemType) {
    const prompt = isEn ? summaryPrompt.en : summaryPrompt.zh;
    setPromptInfo({ prompt, modelType: DEFAULT_MODAL });
    requestGptData();
    changeShowChildren(false);
  }

  function getOperations() {
    if (showChildren) return [];
    if (isEmpty && status === 'loading') return [];
    let list: Array<any> = [];
    if (!isEmpty) {
      list = [copyIcon, fullIcon];
    }

    return list.filter((item) => !hideIcons.includes(item.symbol));
  }

  const { advancedConfirm } = useConfirm({
    filter,
    gameInfo: propsGameInfo,
    setRemainder: () => {},
    modelType: 'streaming',
  });

  async function toggleVisible() {
    const prompt = isEn ? summaryPrompt.en : summaryPrompt.zh;
    try {
      await advancedConfirm({ prompt, modelType: DEFAULT_MODAL });
    } catch (error) {}
    getPageSummary({ prompt, modelType: DEFAULT_MODAL });
  }

  useImperativeHandle(ref, () => ({
    getPageSummary,
    handleStop,
    toggleVisible,
  }));

  return (
    <>
      <CardCommon
        className="dbo-aigc-card-wrapper"
        title={titleInfo}
        loading={loading}
        operations={getOperations()}
        logBtnId="GPT"
        operationInfo={['', () => {}]}
        handleOperationsOnClick={({ symbol }) => {
          if (symbol === 'copy') {
            DBO_T.copyText(summary, formatMessage);
          } else if (symbol === 'refresh') {
            if (isEmpty) changeShowChildren(true);
            else toggleVisible();
          } else if (symbol === 'fullscreen') {
            openFullDialog();
          }
        }}
        {...cardProps}
      >
        {/* !gptDefaultHtml && showChildren ? */}
        {/* showChildren为展示Get AI Summary 按钮  */}
        {showChildren ? (
          children(toggleVisible, { loading, total, handleStop })
        ) : (
          <GptCardView
            status={status}
            total={total}
            percent={percent}
            hasStopBtn={hasStopBtn}
            gptSumaryEndSlot={gptSumaryEndSlot}
            isLittle={total < 10}
            hasTotalText={hasTotalText}
            handleStop={() => {
              handleStop();
              changeShowChildren(true);
            }}
            reload={() => toggleVisible()}
            renderHtml={renderHtml}
            isEmpty={isEmpty}
            isMini={isMini}
            errorType={errorType}
            loadingElement={loadingElement}
            promptInfo={promptInfo}
            leftElement={leftElement}
            loadingTipsText={loadingTipsText}
            failedTipsText={failedTipsText}
          ></GptCardView>
        )}
      </CardCommon>

      {/* <PromptModal
        visible={visible}
        studioGameInfo={propsGameInfo}
        studioWholeGameInfo={propsWholeGameInfo}
        promptList={promptList}
        hasAdvancedSummary={hasAdvancedSummary}
        needBasicTaskId={needBasicTaskId}
        close={() => toggleVisible()}
        scenario={scenario}
        needRequestPrompt={needRequestPrompt}
        promptValue={promptValue}
        previewPrompt={previewPrompt}
        modelType="streaming"
        hasResultOutput={hasResultOutput}
        hasAgent={hasAgent}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleConfirm={async (data, defaultAction) => {
          toggleVisible();
          setShowDefaultGpt(false);
          // 如果有自定义的promptConfirm，优先使用自定义的
          // 自定义返回true就不继续向后走
          const flag = await promptConfirm?.(data, defaultAction);
          if (flag) return;

          if (data.resultType === 'page') {
            getPageSummary(data);
          } else {
            defaultAction?.();
          }
        }}
        filter={filter}
      ></PromptModal> */}
      <FullDialog
        renderHtml={renderHtml}
        summary={summary}
        hideSubTitle={hideFullSubTitle}
      ></FullDialog>
    </>
  );
}

export default forwardRef<PageCardRefType, PageCardType>(
  SteamingPageCard as any,
);
